/**
 * Journal Service
 * Handles storage and retrieval of journal entries
 * Uses localStorage for UI phase (will be replaced with API in Phase 3)
 */

import { JournalEntry, JournalEntryFormData, JournalFilters } from "../types/journal";

const STORAGE_KEY_PREFIX = "tradeLog_journal_";

/**
 * Get storage key for user's journal entries
 */
const getStorageKey = (userId: string): string => {
  return `${STORAGE_KEY_PREFIX}${userId}`;
};

/**
 * Get all journal entries for a user
 */
export const getJournalEntries = (userId: string): JournalEntry[] => {
  try {
    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return [];
    }
    const entries = JSON.parse(stored);
    // Convert date strings back to Date objects
    return entries.map((e: JournalEntry) => ({
      ...e,
      date: new Date(e.date),
      createdAt: new Date(e.createdAt),
      updatedAt: new Date(e.updatedAt),
    }));
  } catch (error) {
    console.error("Error loading journal entries:", error);
    return [];
  }
};

/**
 * Get a single journal entry by ID
 */
export const getJournalEntry = (userId: string, entryId: string): JournalEntry | null => {
  const entries = getJournalEntries(userId);
  return entries.find((e) => e.id === entryId) || null;
};

/**
 * Get journal entries for a specific trade
 */
export const getJournalEntriesForTrade = (userId: string, tradeId: string): JournalEntry[] => {
  const entries = getJournalEntries(userId);
  return entries.filter((e) => e.relatedTradeIds?.includes(tradeId));
};

/**
 * Save a new journal entry
 */
export const saveJournalEntry = (userId: string, entryData: JournalEntryFormData): JournalEntry => {
  const entries = getJournalEntries(userId);
  
  const newEntry: JournalEntry = {
    id: `journal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: entryData.type,
    title: entryData.title,
    content: entryData.content,
    date: new Date(entryData.date),
    relatedTradeIds: entryData.relatedTradeIds || [],
    tags: entryData.tags || [],
    lessonsLearned: entryData.lessonsLearned || [],
    moodRating: entryData.moodRating,
    marketConditions: entryData.marketConditions,
    goals: entryData.goals || [],
    preTradeConfidence: entryData.preTradeConfidence,
    preTradeEmotion: entryData.preTradeEmotion,
    followedPlan: entryData.followedPlan,
    temptedEarlyExit: entryData.temptedEarlyExit,
    addedToPosition: entryData.addedToPosition,
    postTradeEmotion: entryData.postTradeEmotion,
    emotionsInvolved: entryData.emotionsInvolved,
    userId,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  entries.push(newEntry);
  saveJournalEntries(userId, entries);
  
  return newEntry;
};

/**
 * Update an existing journal entry
 */
export const updateJournalEntry = (
  userId: string,
  entryId: string,
  entryData: Partial<JournalEntryFormData>
): JournalEntry | null => {
  const entries = getJournalEntries(userId);
  const index = entries.findIndex((e) => e.id === entryId);
  
  if (index === -1) {
    return null;
  }

  const updatedEntry: JournalEntry = {
    ...entries[index],
    ...(entryData.type && { type: entryData.type }),
    ...(entryData.title && { title: entryData.title }),
    ...(entryData.content && { content: entryData.content }),
    ...(entryData.date && { date: new Date(entryData.date) }),
    ...(entryData.relatedTradeIds !== undefined && { relatedTradeIds: entryData.relatedTradeIds }),
    ...(entryData.tags !== undefined && { tags: entryData.tags }),
    ...(entryData.lessonsLearned !== undefined && { lessonsLearned: entryData.lessonsLearned }),
    ...(entryData.moodRating !== undefined && { moodRating: entryData.moodRating }),
    ...(entryData.marketConditions !== undefined && { marketConditions: entryData.marketConditions }),
    ...(entryData.goals !== undefined && { goals: entryData.goals }),
    ...(entryData.preTradeConfidence !== undefined && { preTradeConfidence: entryData.preTradeConfidence }),
    ...(entryData.preTradeEmotion !== undefined && { preTradeEmotion: entryData.preTradeEmotion }),
    ...(entryData.followedPlan !== undefined && { followedPlan: entryData.followedPlan }),
    ...(entryData.temptedEarlyExit !== undefined && { temptedEarlyExit: entryData.temptedEarlyExit }),
    ...(entryData.addedToPosition !== undefined && { addedToPosition: entryData.addedToPosition }),
    ...(entryData.postTradeEmotion !== undefined && { postTradeEmotion: entryData.postTradeEmotion }),
    ...(entryData.emotionsInvolved !== undefined && { emotionsInvolved: entryData.emotionsInvolved }),
    updatedAt: new Date(),
  };

  entries[index] = updatedEntry;
  saveJournalEntries(userId, entries);
  
  return updatedEntry;
};

/**
 * Delete a journal entry
 */
export const deleteJournalEntry = (userId: string, entryId: string): boolean => {
  const entries = getJournalEntries(userId);
  const filtered = entries.filter((e) => e.id !== entryId);
  
  if (filtered.length === entries.length) {
    return false; // Entry not found
  }

  saveJournalEntries(userId, filtered);
  return true;
};

/**
 * Filter journal entries
 */
export const filterJournalEntries = (
  entries: JournalEntry[],
  filters: JournalFilters
): JournalEntry[] => {
  let filtered = [...entries];

  // Filter by type
  if (filters.type && filters.type !== "all") {
    filtered = filtered.filter((e) => e.type === filters.type);
  }

  // Filter by date range
  if (filters.dateRange?.startDate) {
    filtered = filtered.filter((e) => {
      const entryDate = new Date(e.date);
      return entryDate >= filters.dateRange!.startDate!;
    });
  }
  if (filters.dateRange?.endDate) {
    filtered = filtered.filter((e) => {
      const entryDate = new Date(e.date);
      return entryDate <= filters.dateRange!.endDate!;
    });
  }

  // Filter by tags
  if (filters.tags && filters.tags.length > 0) {
    filtered = filtered.filter((e) => {
      return filters.tags!.some((tag) => e.tags?.includes(tag));
    });
  }

  // Search query
  if (filters.searchQuery) {
    const query = filters.searchQuery.toLowerCase();
    filtered = filtered.filter((e) => {
      return (
        e.title.toLowerCase().includes(query) ||
        e.content.toLowerCase().includes(query) ||
        e.lessonsLearned?.some((lesson) => lesson.toLowerCase().includes(query)) ||
        e.marketConditions?.toLowerCase().includes(query) ||
        e.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    });
  }

  return filtered;
};

/**
 * Get journal entries grouped by date
 */
export const getJournalEntriesByDate = (entries: JournalEntry[]): Map<string, JournalEntry[]> => {
  const grouped = new Map<string, JournalEntry[]>();
  
  entries.forEach((entry) => {
    const dateKey = new Date(entry.date).toISOString().split("T")[0];
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(entry);
  });

  return grouped;
};

/**
 * Get journal entries grouped by week
 */
export const getJournalEntriesByWeek = (entries: JournalEntry[]): Map<string, JournalEntry[]> => {
  const grouped = new Map<string, JournalEntry[]>();
  
  entries.forEach((entry) => {
    const date = new Date(entry.date);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
    const weekKey = weekStart.toISOString().split("T")[0];
    
    if (!grouped.has(weekKey)) {
      grouped.set(weekKey, []);
    }
    grouped.get(weekKey)!.push(entry);
  });

  return grouped;
};

/**
 * Save journal entries to localStorage
 */
const saveJournalEntries = (userId: string, entries: JournalEntry[]): void => {
  try {
    const storageKey = getStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(entries));
  } catch (error) {
    console.error("Error saving journal entries:", error);
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      throw new Error("Storage quota exceeded. Please delete some journal entries.");
    }
    throw new Error("Failed to save journal entry");
  }
};

